'use client';

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import type { BoardCell as BoardCellType } from '@/lib/types';

interface BoardCellProps {
  cell: BoardCellType;
}

export function BoardCell({ cell }: BoardCellProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Handle rendering of different cell types
  useEffect(() => {
    if (!contentRef.current) return;

    // For formula type, try to render with KaTeX
    if (cell.type === 'formula' && cell.metadata.libs?.includes('katex')) {
      import('katex').then((katex) => {
        if (contentRef.current) {
          try {
            contentRef.current.innerHTML = katex.default.renderToString(cell.content, {
              throwOnError: false,
              displayMode: true,
            });
          } catch {
            contentRef.current.textContent = cell.content;
          }
        }
      });
    }
    // For other types or plain text, just set the HTML
    else if (cell.type === 'text') {
      contentRef.current.textContent = cell.content;
    } else {
      // HTML content (diagrams, interactive, etc.)
      contentRef.current.innerHTML = cell.content;
    }
  }, [cell.content, cell.type, cell.metadata.libs]);

  const authorLabel = cell.author === 'teacher' ? '🤖 Teacher' : '📝 You';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`board-cell ${cell.author === 'teacher' ? 'by-teacher' : 'by-student'}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
        <span>{authorLabel}</span>
        <span>{new Date(cell.timestamp).toLocaleTimeString()}</span>
      </div>

      {/* Content */}
      <div ref={contentRef} className="cell-content" />

      {/* Metadata hint (for debugging/accessibility) */}
      {cell.metadata.description && (
        <div className="mt-2 text-xs text-muted-foreground italic">
          {cell.metadata.description}
        </div>
      )}
    </motion.div>
  );
}
